import type { FC } from 'react';
import { useMemo } from 'react';
import DownIcon from '@mui/icons-material/KeyboardArrowDown';
import RightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Stack, Typography } from '@mui/material';
import { useAtom } from 'jotai';
import _ from 'lodash';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import useGuidedTour from 'src/components/app/context/GuidedTourContext';
import { dashboardHiddenSections } from 'src/components/dashboard/DashboardPage/atom';

import { styles } from './index.styles';

export const SECTION_WIDGET_TEST_ID = 'section-widget';

const SectionWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const [hiddenSections, setHiddenSections] = useAtom(dashboardHiddenSections);
  const indexOfSectionId = hiddenSections.indexOf(config.id);
  const isHidden = indexOfSectionId !== -1;
  const { createStep } = useGuidedTour();
  const guidedTour = useMemo(
    () => createStep(config.id, config.guided_tour_step),
    [config.guided_tour_step],
  );
  const toggleSection = () => {
    setHiddenSections((prevState) =>
      isHidden
        ? prevState.filter((sectionId) => sectionId !== config.id)
        : _.union([config.id], prevState),
    );
  };

  return (
    <Stack direction="row" className="SectionWidget-root">
      {guidedTour}
      <Stack
        direction="row"
        sx={styles.sectionWidget}
        onClick={toggleSection}
        onKeyDown={(e) => {
          if (['Enter', 'Space'].includes(e.code)) {
            e.preventDefault();
            toggleSection();
          }
        }}
        data-test-id={SECTION_WIDGET_TEST_ID}
        tabIndex={0}
        role="button"
      >
        {isHidden ? (
          <RightIcon color="primary" fontSize="large" />
        ) : (
          <DownIcon color="primary" fontSize="large" />
        )}

        <Typography variant="h2">{config.widget_text}</Typography>
      </Stack>
    </Stack>
  );
};

export default SectionWidget;
